import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, CalendarClock } from 'lucide-react';
import { fetchMaliTakvimFull } from '../../api/dashboard';
import type { MaliTakvimDynamicItem, MaliTakvimItem } from '../../api/dashboard';

/** Kategori → rozet rengi (MaliTakvimPage ile uyumlu). */
const CATEGORY_CLASSES: Record<string, string> = {
  beyanname: 'bg-blue-50 text-blue-700',
  vergi: 'bg-violet-50 text-violet-700',
  sgk: 'bg-cyan-50 text-cyan-700',
  cek: 'bg-amber-50 text-amber-700',
  teminat: 'bg-emerald-50 text-emerald-700',
  taksit: 'bg-pink-50 text-pink-700',
  belge: 'bg-slate-100 text-slate-600',
  izin: 'bg-orange-50 text-orange-700',
  ekipman: 'bg-teal-50 text-teal-700',
  diger: 'bg-slate-100 text-slate-600',
};

const CATEGORY_LABELS: Record<string, string> = {
  beyanname: 'Beyanname',
  vergi: 'Vergi',
  sgk: 'SGK',
  cek: 'Çek/Senet',
  teminat: 'Teminat',
  taksit: 'Taksit',
  belge: 'Belge',
  izin: 'İş İzni',
  ekipman: 'Ekipman',
  diger: 'Diğer',
};

function shortDate(value: string): string {
  const datePart = value.slice(0, 10);
  const [y, m, d] = datePart.split('-').map(Number);
  if (!y || !m || !d) return value;
  return new Date(y, m - 1, d).toLocaleDateString('tr-TR', { day: 'numeric', month: 'short' });
}

function daysLabel(daysLeft: number): { text: string; className: string } {
  if (daysLeft < 0) return { text: `${Math.abs(daysLeft)} gün gecikti`, className: 'text-red-600' };
  if (daysLeft === 0) return { text: 'BUGÜN', className: 'font-bold text-red-600' };
  if (daysLeft <= 7) return { text: `${daysLeft} gün`, className: 'font-semibold text-amber-600' };
  return { text: `${daysLeft} gün`, className: 'text-slate-500' };
}

interface UpcomingItem {
  title: string;
  date: string;
  days_left: number;
  kategori: string;
  url?: string;
}

/**
 * Dashboard "Mali Takvim & Gelişmeler" özeti: yaklaşan 5 yükümlülük
 * (yasal + şirket vadeleri karışık, tarih sıralı) + son mali haberler.
 * Detay sayfası: /mali-takvim.
 */
export function MaliSummaryCard() {
  const [upcoming, setUpcoming] = useState<UpcomingItem[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const today = new Date();
    const fmt = (d: Date) =>
      `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
    const end = new Date(today);
    end.setDate(end.getDate() + 45);

    fetchMaliTakvimFull({ start: fmt(today), end: fmt(end) })
      .catch(() => null)
      .then((takvim) => {
        if (takvim) {
          const merged: UpcomingItem[] = [
            ...takvim.data.map((i: MaliTakvimItem) => ({
              title: i.title,
              date: i.date,
              days_left: i.days_left,
              kategori: i.kategori ?? 'diger',
            })),
            ...takvim.dynamic.map((i: MaliTakvimDynamicItem) => ({
              title: i.title,
              date: i.date,
              days_left: i.days_left,
              kategori: i.kategori,
              url: i.url,
            })),
          ];
          merged.sort((a, b) => a.date.localeCompare(b.date));
          setUpcoming(merged.slice(0, 5));
        }
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return null;

  return (
    <div className="rounded-xl bg-white p-5 shadow-sm" data-testid="dashboard-mali-summary">
      {/* Yaklaşan yükümlülükler */}
      <div className="mb-2 flex items-center justify-between">
        <h3 className="flex items-center gap-1.5 text-sm font-semibold text-slate-800">
          <CalendarClock className="h-4 w-4 text-blue-500" />
          Yaklaşan Mali Yükümlülükler
        </h3>
        <Link
          to="/mali-takvim"
          data-testid="dashboard-mali-summary-all"
          className="inline-flex items-center gap-1 text-xs font-semibold text-blue-600 hover:text-blue-800"
        >
          Mali Takvim
          <ArrowRight className="h-3 w-3" />
        </Link>
      </div>

      {upcoming.length === 0 ? (
        <p className="py-2 text-xs text-slate-400" data-testid="dashboard-mali-summary-empty">
          Önümüzdeki 45 günde yükümlülük yok.
        </p>
      ) : (
        <div className="space-y-1.5">
          {upcoming.map((item, i) => {
            const days = daysLabel(item.days_left);
            const inner = (
              <>
                <span
                  className={`flex-none rounded-full px-2 py-0.5 text-[10px] font-semibold ${CATEGORY_CLASSES[item.kategori] ?? CATEGORY_CLASSES.diger}`}
                >
                  {CATEGORY_LABELS[item.kategori] ?? item.kategori}
                </span>
                <span className="min-w-0 flex-1 truncate text-xs text-slate-700">{item.title}</span>
                <span className="flex-none text-[11px] text-slate-400">{shortDate(item.date)}</span>
                <span className={`flex-none text-[11px] ${days.className}`}>{days.text}</span>
              </>
            );
            return item.url ? (
              <Link
                key={i}
                to={item.url}
                data-testid={`dashboard-mali-upcoming-${i}`}
                className="flex items-center gap-2 rounded-lg px-2 py-1.5 hover:bg-slate-50"
              >
                {inner}
              </Link>
            ) : (
              <div key={i} data-testid={`dashboard-mali-upcoming-${i}`} className="flex items-center gap-2 px-2 py-1.5">
                {inner}
              </div>
            );
          })}
        </div>
      )}

    </div>
  );
}